import Vue from 'vue';
import {NavigationDrawerAccess} from '@/enums/_common/NavigationDrawerAccess.enum';

export type ShellBadge = {
    value: string | number;
    description: string;
    /** Tone class of modern.css */
    tone: string;
};

/** Flat menu entry with resolved link, used for favorites (also pinned profiles), recent visits and the filter */
export type ShellNavEntry = {
    text: string;
    icon: string;
    to: string;
    /** Name of the menu group, empty for top-level entries */
    groupText: string;
    item: NavigationDrawerAccess.DrawerDataItem;
};

const STORAGE_PREFIX = 'vanilla.shell.nav.';
const MAX_RECENT = 4;
/** With this many menu entries or fewer the whole menu is visible at a glance, so recent pages would only repeat it */
const MIN_ENTRIES_FOR_RECENT = 9;

const toneOfColor = (color: string | undefined): string => {
    if (color === 'success') {
        return 'tone-good';
    }
    if (color === 'error') {
        return 'tone-bad';
    }
    if (color === 'warning') {
        return 'tone-warn';
    }
    return 'tone-info';
};

const resolve = <T>(value: T | (() => T)): T => {
    return typeof value === 'function' ? (value as () => T)() : value;
};

export const resolveLink = (item: NavigationDrawerAccess.DrawerDataItem): string | null => {
    return resolve(item.to);
};

/** Badge of a menu entry, only if it has a value (counts of 0 are hidden like before) */
export const resolveBadge = (item: NavigationDrawerAccess.DrawerDataItem): ShellBadge | null => {
    if (!item.badge) {
        return null;
    }
    const value = resolve(item.badge.value);
    if (!value) {
        return null;
    }
    const description = item.badge.description ? resolve(item.badge.description) : null;
    return {value, description: description ? String(description) : '', tone: toneOfColor(item.badge.color)};
};

/** Badge shown on a closed group or in the collapsed sidebar: the highest count of its entries */
export const groupBadge = (group: NavigationDrawerAccess.DrawerGroupItem): ShellBadge | null => {
    let best: ShellBadge | null = null;
    for (const child of group.items) {
        if (child.type !== 'item') {
            continue;
        }
        const badge = resolveBadge(child);
        if (badge && (!best || Number(badge.value) > Number(best.value))) {
            best = badge;
        }
    }
    return best;
};

export const flattenEntries = (items: NavigationDrawerAccess.DrawerItem[]): ShellNavEntry[] => {
    const result: ShellNavEntry[] = [];
    const add = (item: NavigationDrawerAccess.DrawerDataItem, groupText: string) => {
        const to = resolveLink(item);
        if (to) {
            result.push({text: item.text, icon: item.icon, to, groupText, item});
        }
    };
    for (const item of items) {
        if (item.type === 'group') {
            item.items.forEach((child) => child.type === 'item' && add(child, item.text));
        } else {
            add(item, '');
        }
    }
    return result;
};

const normalize = (text: string): string => text.toLocaleLowerCase('de').normalize('NFD').replace(/[̀-ͯ]/g, '');

/** Entries whose name (or group name) contains the filter text */
export const filterEntries = (entries: ShellNavEntry[], query: string): ShellNavEntry[] => {
    const q = normalize(query.trim());
    if (!q) {
        return entries;
    }
    return entries.filter((entry) => normalize(entry.text).includes(q) || normalize(entry.groupText).includes(q));
};

/** The menu entry belonging to a route: longest link that is a prefix of the path ("/" only matches exactly) */
export const entryOfPath = (entries: ShellNavEntry[], path: string): ShellNavEntry | null => {
    let best: ShellNavEntry | null = null;
    for (const entry of entries) {
        const matches = entry.to === '/' ? path === '/' : path === entry.to || path.startsWith(`${entry.to}/`);
        if (matches && (!best || entry.to.length > best.to.length)) {
            best = entry;
        }
    }
    return best;
};

const read = (key: string): string[] => {
    try {
        const raw = localStorage.getItem(STORAGE_PREFIX + key);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.filter((el) => typeof el === 'string') : [];
    } catch {
        return [];
    }
};

const write = (key: string, values: string[]) => {
    try {
        localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(values));
    } catch {
        // Storage not available (private mode): favorites only last for this session
    }
};

/** Profiles that can be pinned as favorites: link prefix, icon of the list in the menu and the kind shown after the name */
export type ShellProfileKind = 'teacher' | 'employee' | 'applicant' | 'institution' | 'student' | 'parent';

const PROFILE_KINDS: Record<ShellProfileKind, { prefix: string; menuKey: NavigationDrawerAccess; label: string }> = {
    teacher: {prefix: '/teachers', menuKey: NavigationDrawerAccess.TEACHERS, label: 'Lehrkraft'},
    employee: {prefix: '/employees', menuKey: NavigationDrawerAccess.EMPLOYEES, label: 'Mitarbeitende:r'},
    applicant: {prefix: '/recruiter/applicants', menuKey: NavigationDrawerAccess.JOB_ADS_APPLICANTS, label: 'Bewerber:in'},
    institution: {prefix: '/institutions', menuKey: NavigationDrawerAccess.INSTITUTIONS, label: 'Standort'},
    student: {prefix: '/students', menuKey: NavigationDrawerAccess.STUDENTS, label: 'Schüler:in'},
    parent: {prefix: '/parents', menuKey: NavigationDrawerAccess.PARENTS, label: 'Elternteil'},
};

/** Pinned profile; the name is the one of the last visit (the profile header refreshes it) */
export type ShellProfileFavorite = { kind: ShellProfileKind; text: string };

export const profileLink = (kind: ShellProfileKind, id: string): string => `${PROFILE_KINDS[kind].prefix}/${id}`;

/** Id of the profile a path belongs to (also on its sub pages), null outside the profiles */
export const profileIdOfPath = (path: string): string | null => {
    for (const kind of Object.values(PROFILE_KINDS)) {
        if (path.startsWith(`${kind.prefix}/`)) {
            return path.slice(kind.prefix.length + 1).split('/')[0] || null;
        }
    }
    return null;
};

/**
 * Link of the pinned profile with this id. Profile ids are unique, so the same person under /teachers, /employees and
 * /recruiter/applicants is one favorite, kept under the link it was pinned with
 */
export const pinnedProfileLink = (links: string[], profiles: Record<string, ShellProfileFavorite>, id: string): string | null => {
    return links.find((to) => !!profiles[to] && profileIdOfPath(to) === id) || null;
};

/** Sidebar entry of a pinned profile, null for unknown kinds (e.g. stored by a newer version) */
export const profileEntry = (to: string, profile: ShellProfileFavorite): ShellNavEntry | null => {
    const kind = PROFILE_KINDS[profile.kind];
    if (!kind) {
        return null;
    }
    const icon = NavigationDrawerAccess.Record[kind.menuKey].icon;
    return {text: profile.text, icon, to, groupText: kind.label, item: {type: 'item', text: profile.text, icon, to}};
};

const readProfiles = (key: string): Record<string, ShellProfileFavorite> => {
    try {
        const raw = localStorage.getItem(STORAGE_PREFIX + key);
        const parsed = raw ? JSON.parse(raw) : {};
        const result: Record<string, ShellProfileFavorite> = {};
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            for (const [to, value] of Object.entries(parsed as Record<string, any>)) {
                if (value && typeof value.kind === 'string' && typeof value.text === 'string') {
                    result[to] = {kind: value.kind, text: value.text};
                }
            }
        }
        return result;
    } catch {
        return {};
    }
};

const writeProfiles = (key: string, profiles: Record<string, ShellProfileFavorite>) => {
    try {
        localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(profiles));
    } catch {
        // Storage not available (private mode): favorites only last for this session
    }
};

/**
 * Favorites of the logged-in user: links in their order (menu entries and profiles) and the names of the pinned profiles.
 * Shared by the sidebar and the stars in the profile headers
 */
export const shellFavorites = Vue.observable({
    links: [] as string[],
    profiles: {} as Record<string, ShellProfileFavorite>,
});

const storeFavorites = (userId: string, links: string[], profiles: Record<string, ShellProfileFavorite>) => {
    write(`fav.${userId}`, links);
    writeProfiles(`favProfiles.${userId}`, profiles);
    shellFavorites.links = links;
    shellFavorites.profiles = profiles;
};

/** Favorites, recent visits and collapsed sections of the menu, per user and only in this browser */
export const ShellNavMemory = {
    loadFavorites(userId: string) {
        shellFavorites.links = read(`fav.${userId}`);
        shellFavorites.profiles = readProfiles(`favProfiles.${userId}`);
    },
    /** Pins or unpins a menu entry or profile; unpinning a profile also forgets its name */
    toggleFavorite(userId: string, to: string) {
        const current = read(`fav.${userId}`);
        const profiles = readProfiles(`favProfiles.${userId}`);
        if (current.includes(to)) {
            delete profiles[to];
            storeFavorites(userId, current.filter((el) => el !== to), profiles);
        } else {
            storeFavorites(userId, [...current, to], profiles);
        }
    },
    /** Pins the profile or unpins it, also when it was pinned under another link (see pinnedProfileLink) */
    toggleProfileFavorite(userId: string, id: string, profile: ShellProfileFavorite) {
        const current = read(`fav.${userId}`);
        const profiles = readProfiles(`favProfiles.${userId}`);
        const pinned = pinnedProfileLink(current, profiles, id);
        if (pinned) {
            ShellNavMemory.toggleFavorite(userId, pinned);
            return;
        }
        const to = profileLink(profile.kind, id);
        storeFavorites(userId, [...current, to], {...profiles, [to]: profile});
    },
    /** Takes the current name of a pinned profile (renamed since it was pinned); the pinned link stays */
    refreshProfileFavorite(userId: string, id: string, text: string) {
        const current = read(`fav.${userId}`);
        const profiles = readProfiles(`favProfiles.${userId}`);
        const pinned = pinnedProfileLink(current, profiles, id);
        if (!pinned || profiles[pinned].text === text) {
            return;
        }
        storeFavorites(userId, current, {...profiles, [pinned]: {...profiles[pinned], text}});
    },
    /** Stores the favorites in the given order */
    setFavorites(userId: string, favorites: string[]) {
        storeFavorites(userId, favorites, readProfiles(`favProfiles.${userId}`));
    },
    recent(userId: string): string[] {
        return read(`recent.${userId}`);
    },
    addRecent(userId: string, to: string): string[] {
        const next = [to, ...read(`recent.${userId}`).filter((el) => el !== to)].slice(0, MAX_RECENT + 4);
        write(`recent.${userId}`, next);
        return next;
    },
    collapsedSections(userId: string): string[] {
        return read(`collapsed.${userId}`);
    },
    toggleCollapsedSection(userId: string, section: string): string[] {
        const current = read(`collapsed.${userId}`);
        const next = current.includes(section) ? current.filter((el) => el !== section) : [...current, section];
        write(`collapsed.${userId}`, next);
        return next;
    },
    MAX_RECENT,
    MIN_ENTRIES_FOR_RECENT,
};

/** Menu section an entry was clicked in; the same page can be listed in several sections */
export type ShellNavSource = 'favorites' | 'recent' | 'menu';

const NAV_SOURCE_KEY = 'shellNavSource';

/**
 * Section the current page was opened from. Kept in the browser history entry (history.state), so reload and
 * back/forward show the same section again; navigation from elsewhere has none and the menu entry stays highlighted
 */
export const shellNavSource = Vue.observable({value: null as ShellNavSource | null});

/** Stores the clicked section in the current history entry; vue-router keeps foreign state keys on replace */
export const markNavSource = (source: ShellNavSource) => {
    try {
        window.history.replaceState({...window.history.state, [NAV_SOURCE_KEY]: source}, '');
    } catch {
        // History not writable: the highlight only lasts until the next reload
    }
    shellNavSource.value = source;
};

/** Section of a click in the sidebar whose navigation is still running */
let expectedSource: ShellNavSource | null = null;

/**
 * Remembers the clicked section before navigating, so the new page is highlighted there right away instead of first
 * in the menu; null when the navigation failed
 */
export const expectNavSource = (source: ShellNavSource | null) => {
    expectedSource = source;
};

/** Reads the section of the current history entry, e.g. after back/forward or reload */
export const readNavSource = () => {
    if (expectedSource) {
        markNavSource(expectedSource);
        expectedSource = null;
        return;
    }
    const value = window.history.state?.[NAV_SOURCE_KEY];
    shellNavSource.value = value === 'favorites' || value === 'recent' || value === 'menu' ? value : null;
};

/** Environment shown next to the logo; nothing for production */
export const environmentLabel = (apiPath: string | undefined): string => {
    const path = (apiPath || '').toLowerCase();
    if (path.includes('localhost') || path.includes('127.0.0.1')) {
        return 'lokal';
    }
    if (path.includes('testing')) {
        return 'testing';
    }
    if (path.includes('staging')) {
        return 'staging';
    }
    return '';
};
